import { Dispatch, useReducer } from "react";
import { ISavedSearchItem } from "../save-search.types";
import { ViewportSizeType } from "../../hooks/useViewport.hook";

export interface ISaveSearchModalPayload {
  searchName?: string;
  isValid?: boolean;
  isAlerts?: boolean;
  alertDeactivationDate?: string;
  deleteAlert?: boolean;
  isSaved?: boolean;
  isError?: boolean;
}

type ActionStateType = "inputting" | "saved" | "confirmed" | "error" | "clear";

export interface ISaveSearchInputAction {
  type: ActionStateType,
  state?: ISaveSearchModalPayload
}

export interface SaveSearchModalProps {
  isOpen: boolean;
  onClose?: () => void;
  savedSearches: ISavedSearchItem[];
  viewport: ViewportSizeType;
}

const initialState: ISaveSearchModalPayload = {
  searchName: "",
  isValid: false,
  isAlerts: false,
  alertDeactivationDate: null,
  deleteAlert: false,
  isSaved: false,
  isError: false
}

function useSaveSearchReducer(): [ISaveSearchModalPayload, Dispatch<ISaveSearchInputAction>] {
  const [ state, dispatch ] = useReducer(updateState, initialState);

  function updateState(state: ISaveSearchModalPayload, action: ISaveSearchInputAction): ISaveSearchModalPayload {

    switch (action.type) {
      case "inputting": {
        const actionState = action.state;

        // Resolve user input...
        const searchName = actionState?.searchName != null ? actionState.searchName : state.searchName;
        const isValid = actionState?.isValid != null ? actionState.isValid : state.isValid;

        // Resolve alerts flag...
        const isAlerts = actionState?.isAlerts != null ? actionState.isAlerts : state.isAlerts;

        // Resolve deactivation date...
        // Note. It should only been undefined when initialised; before a date has been set.
        // When a date is passed as null, this denotes the date has been unset by a user and 
        // we want to apply that null value.
        const alertDeactivationDate = actionState?.alertDeactivationDate !== undefined
          ? actionState.alertDeactivationDate
          : state.alertDeactivationDate;
        
        // Resolve delete flag...
        let deleteAlert = false;
        if (alertDeactivationDate != null) {
          deleteAlert = actionState?.deleteAlert != null ? actionState.deleteAlert : state.deleteAlert;
        }

        return { ...state, searchName, isValid, isAlerts, alertDeactivationDate, deleteAlert };
      }
      case "saved": {
        return { ...state, isSaved: true }
      }
      case "confirmed": {
        // Confirmed should essentially give us a clean slate
        return initialState;
      }
      case "clear": {
        return initialState;
      }
      case "error": {
        return { ...state, isError: true }
      }
      default: { // clear
        return initialState;
      }
    }
  }

  return [state, dispatch];
}

export default useSaveSearchReducer;
